import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { IMAGE_FIELDS, IMAGE_VARIANTS } from '../../util/constants';
import { ListingState, UserType } from '../../util/enums';
import { storableError } from '../../util/errors';
import { getImageVariantConfigs } from '../../util/genericHelpers';
import { parse } from '../../util/urlHelpers';

export const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === ListingState.PUBLISHED)
    .map(l => l.id);
};
const RESULT_PAGE_SIZE = 60;
// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/TalentOfferingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/TalentOfferingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/TalentOfferingPage/SEARCH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const currentPageResultIdsSelector = state => state.TalentOfferingPage.currentPageResultIds;
export const paginationSelector = state => state.TalentOfferingPage.pagination;
// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const searchListings = (searchParams, config) => async (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));
  try {
    // Extract image configuration
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = IMAGE_VARIANTS.PREFIX,
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    // Build API parameters
    const params = {
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        ...IMAGE_FIELDS,
        `variants.${variantPrefix}`, // Cropped variants
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      ...getImageVariantConfigs(variantPrefix, aspectRatio),
    };
    const updatedParams = { ...params, ...searchParams };
    // Make API call
    const response = await sdk.listings.query(updatedParams);

    // Process response
    const listingFields = config?.listing?.listingFields || [];
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchListingsSuccess(response));

    return response;
  } catch (error) {
    const errorToStore = storableError(error);
    dispatch(searchListingsError(errorToStore));
    throw errorToStore;
  }
};

export const loadData = (params, search, config) => dispatch => {
  const queryParams = parse(search);
  const { page = 1, pub_pub_sports, pub_enhancedServicesSubTypes, ...rest } = queryParams;
  const searchParams = {
    ...rest,
    page,
    pub_listingCategory: UserType.TALENT,
    ...(pub_pub_sports ? { pub_pub_sports: `has_any:${pub_pub_sports}` } : {}),
    ...(pub_enhancedServicesSubTypes
      ? { pub_enhancedServicesSubTypes: `has_any:${pub_enhancedServicesSubTypes}` }
      : {}),
  };
  return dispatch(searchListings(searchParams, config));
};
